// stores/vehicleStatusStore.js
'use client';
import { useState, useEffect, useCallback, useRef } from 'react';

import { fleetApi } from '../api/axiosClient';

import { fetchVssDeviceStatusBulk } from '../utils/vssHelper';
import { timezone } from '../utils/timeZone';

/**
 * Utilities
 */
const getTzParam = (tz = null) => {
  const defaultTz = typeof timezone === 'function' ? timezone() : Number(timezone);
  const finalTz =
    tz !== undefined && tz !== null && !isNaN(tz)
      ? Number(tz)
      : !isNaN(defaultTz)
        ? defaultTz
        : Math.abs(new Date().getTimezoneOffset() / 60);
  return `?tz=${finalTz}`;
};

/**
 * Helper to normalize the `/get-data/vehicle-status` item to a convenient UI object
 */
const normalizeGetDataItem = (item = {}) => {
  // prefer top-level fields, fallback to join/info where appropriate
  const info = item.info ?? {};
  const join = item.join ?? {};

  const id = info.id ?? item.vehicle ?? null;
  const company = item.company ?? info.company ?? null;
  const pool_name = item.pool_name ?? info.join?.pool_name ?? join.pool_name ?? '';
  const company_name = item.company_name ?? info.join?.company_name ?? join.company_name ?? '';
  const vehicle_license_plate = item.vehicle_license_plate ?? join.vehicle_license_plate ?? info.license_plate ?? '';
  const vehicle_name = item.vehicle_name ?? join.vehicle_name ?? info.name ?? vehicle_license_plate;
  const vehicle_type_name = item.vehicle_type_name ?? info.vehicle_type_name ?? '';
  const last_update = item.last_update ?? info.modified_date ?? null;
  const lat = item.lat ?? info.latitude ?? null;
  const lon = item.lon ?? info.longitude ?? null;
  const status = item.status ?? (info.status === true ? 'active' : 'inactive') ?? '';
  const speed = item.speed ?? 0;

  return {
    raw: item,
    id,
    company,
    company_name,
    pool_name,
    vehicle_license_plate,
    vehicle_name,
    vehicle_type_name,
    last_update,
    lat,
    lon,
    status,
    speed,
    // frequently useful extras:
    driver: item.driver ?? info.driver ?? null,
    imei_no: item.imei_no ?? '',
    gsm_no: item.gsm_no ?? '',
    gps: item.gps ?? '',
    engine: item.engine ?? '',
    batt_volt: item.batt_volt ?? '',
    position: item.position ?? '',
    gsm_signal: item.gsm_signal ?? '',
    location: item.location ?? '',
    join,
    info
  };
};

export const useVehicleStatusDashboardDynamic = (limit = 100, tz = null, refreshIntervalMs = 30000) => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  // isLoading diset false jika sudah pernah load agar UI tidak kedap-kedip
  const [isLoading, setLoading] = useState(true);
  const [isError, setError] = useState(null);

  // Menggunakan ref untuk menyimpan status mount agar menghindari memory leak
  const isMounted = useRef(true);
  const hasLoaded = useRef(false);

  // Ekstrak fungsi load agar bisa dipanggil saat pertama kali dan saat interval
  const loadData = useCallback(async () => {
    try {
      // Hanya set loading true jika belum pernah load
      // agar tidak ada efek kedip di map tiap 30 detik
      if (!hasLoaded.current) setLoading(true);

      const countRes = await fleetApi.get(`/get-data/count-vehicle-status${getTzParam(tz)}`);

      // Ambil total record (antisipasi bentuk response)
      let totalCount = 0;
      if (countRes && typeof countRes === 'object') {
        totalCount = countRes.totalRecord ?? countRes.data?.totalRecord ?? countRes.total ?? countRes.count ?? 0;
      } else if (typeof countRes === 'number') {
        totalCount = countRes;
      }

      if (isMounted.current) setTotal(totalCount);

      let merged = [];
      let offset = 0;

      // Jika totalCount 0, kita keluar (tidak ada data)
      if (totalCount === 0) {
        if (isMounted.current) {
          setData([]);
          setLoading(false);
        }
        return;
      }

      while (offset < totalCount) {
        // Cek jika komponen unmount saat loop berjalan
        if (!isMounted.current) break;

        const tzQuery = getTzParam(tz);
        const url = `/get-data/vehicle-status${tzQuery}&limit=${limit}&offset=${offset}`;
        const r = await fleetApi.get(url);

        const arr = Array.isArray(r.data) ? r.data : (r.data?.data ?? r.data?.items ?? []);
        if (arr.length === 0) break;

        const chunk = arr.map((a) => normalizeGetDataItem(a));

        merged = [...merged, ...chunk];

        // Jika jumlah data yang didapat lebih sedikit dari limit, berarti sudah halaman terakhir
        if (arr.length < limit) break;

        offset += limit;
      }

      // Deduplikasi data merged berdasarkan identifier unik kendaraan
      const vehicleMap = new Map();
      for (const item of merged) {
        const uniqueKey =
          item.id != null
            ? String(item.id)
            : item.raw?.vehicle != null
              ? String(item.raw.vehicle)
              : item.vehicle_license_plate
                ? `${item.vehicle_license_plate}-${item.imei_no || ''}`
                : JSON.stringify(item);

        if (!vehicleMap.has(uniqueKey)) {
          vehicleMap.set(uniqueKey, item);
        } else {
          // Jika ada record ganda, simpan yang lebih mutakhir
          const existing = vehicleMap.get(uniqueKey);
          const existingTime = existing.last_update ? new Date(existing.last_update).getTime() : 0;
          const newTime = item.last_update ? new Date(item.last_update).getTime() : 0;
          if (newTime >= existingTime) {
            vehicleMap.set(uniqueKey, item);
          }
        }
      }
      merged = Array.from(vehicleMap.values());

      // Tampilkan data kendaraan terlebih dahulu agar UI tidak terhambat jika request VSS lama/pending
      if (isMounted.current && !hasLoaded.current) {
        setData(merged);
        setTotal(merged.length);
        setError(null);
        hasLoaded.current = true;
      }

      // Fetch VSS Status for cameras (abort otomatis jika > 10 detik)
      const imeis = merged
        .map((v) => v.raw?.join?.integration_imei || v.info?.integration_imei || v.raw?.integration_imei || v.imei_no)
        .filter(Boolean);

      if (imeis.length > 0) {
        const vssMap = await fetchVssDeviceStatusBulk(imeis, 10000);
        merged = merged.map((v) => {
          const imei = v.raw?.join?.integration_imei || v.info?.integration_imei || v.raw?.integration_imei || v.imei_no;
          if (imei && vssMap[imei]) {
            return { ...v, vssStatus: vssMap[imei] };
          }
          return v;
        });
      }

      if (isMounted.current) {
        setData(merged);
        setTotal(merged.length);
        setError(null);
        hasLoaded.current = true;
      }
    } catch (err) {
      if (isMounted.current) setError(err);
      console.error('useVehicleStatusDashboardDynamic: Failed to fetch', err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [limit, tz]);

  // Effect untuk Lifecycle & Polling
  useEffect(() => {
    isMounted.current = true;

    // 1. Panggil loadData saat komponen pertama kali dirender
    loadData();

    // 2. Set Interval untuk memanggil loadData setiap `refreshIntervalMs`
    let intervalId = null;
    if (refreshIntervalMs > 0) {
      intervalId = setInterval(() => {
        loadData();
      }, refreshIntervalMs);
    }

    // 3. Cleanup function saat unmount
    return () => {
      isMounted.current = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [loadData, refreshIntervalMs]);

  // Mengembalikan `refetch` jika Anda ingin memicu refresh secara manual di UI (misal via tombol)
  return {
    data,
    total,
    isLoading,
    isError,
    refetch: loadData
  };
};
